import { ShadesOrientation } from '../Components/InteriorShadingType.js'
import { Units, getLabel, } from '../Common/Units.js'
import { clamp } from '../Base.js'

import {
  Rect,
} from '../Common/Math.js'

/**
 * Helper class to calculate the shaded and non-shaded areas of a window or door
 * with interior and exterior shading.
 * 
 * Note - the windorOrDoor passed in must be on a vertical wall (calculations assume that).
 */
export class ShadedAreasCalculator {
  constructor(ctx) {
    this.ctx = ctx;
  }

  calcInteriorShadingAreas(windowOrDoor, hr) {
    let windowType = windowOrDoor.getWindowType()
    let optInShadingType = windowOrDoor.getInteriorShadingType()
    let optInShadingSchedule = windowOrDoor.getInteriorShadingSchedule()

    let ctx = this.ctx;
    ctx.startSection(`${windowOrDoor.getTypeName()} Interior Shading Areas`)
    ctx.W = windowType.width.value;
    ctx.H = windowType.height.value;

    if (optInShadingType) {
      ctx.log("Using interior shading")
      if (optInShadingSchedule) {
        ctx.log(`Using internal shading schedule`)
        ctx.full_sched = optInShadingSchedule.getData();
        ctx.sched_hr = ctx.full_sched[hr]
      } else {
        ctx.log("No schedule, using value 1.0 (fully on / shaded)")
        ctx.sched_hr = 1.0;
      }
      let isHorizontal = optInShadingType.orientation.value === ShadesOrientation.Horizontal;
      if (isHorizontal) {
        ctx.log("Horizontal shading - comes down from top")
        ctx.rect_clear = Rect.createFromPoints(0, 0, ctx.W, ctx.H*(1.0 - ctx.sched_hr))
        ctx.rect_shaded = Rect.createFromPoints(0, ctx.H*(1.0 - ctx.sched_hr), ctx.W, ctx.H)
      } else {
        ctx.log("Vertical shading - comes in from left and right sides")
        // The shade comes in from the left and right, so W*sched*0.5 from left and
        // W*sched*0.5 from right are shaded.
        /*
        let lenShaded = ctx.W * ctx.sched_hr * 0.5;
        ctx.rect_clear = Rect.createFromPoints(lenShaded, 0, ctx.W - lenShaded, ctx.H)
        */
       // TODO - for now, just assume in comes in left to right
       ctx.rect_clear = Rect.createFromPoints(ctx.W*ctx.sched_hr, 0, ctx.W, ctx.H)
       ctx.rect_shaded = Rect.createFromPoints(0, 0, ctx.W*ctx.sched_hr, ctx.H)
      }
    } else {
      ctx.log("No interior shading");
      ctx.rect_clear = Rect.createFromPoints(0, 0, ctx.W, ctx.H)
      ctx.rect_shaded = Rect.empty()
    }
    let res = {
      Rect_clear: ctx.rect_clear,
      Rect_shaded: ctx.rect_shaded,
      debug: {
      }
    }
    ctx.endSection()
    return res
  }

  calcExteriorShadingAreas(windowOrDoor, solarAngles) {
    let ctx = this.ctx;

    let windowType = windowOrDoor.getWindowType()
    let optExShadingType = windowOrDoor.getExteriorShadingType()

    ctx.startSection(`${windowOrDoor.getTypeName()} Exterior Shading Areas`)

    // Early out if the window is not in the sun
    if (!(solarAngles.isSunVisibleInSky() && solarAngles.isSurfaceInSun())) {
      ctx.log("This surface is fully shaded (sun down or not in sun).")
      ctx.endSection()
      return {
        Rect_clear: Rect.empty(),
        debug: {
        }
      }
    } else if (solarAngles.solar_altitude_rads == Math.PI / 2.0) {
      ctx.log("Solar altitude is 90 degrees (directly overhead). No shading for vertical wall.")
      ctx.endSection()
      return {
        Rect_clear: Rect.empty(),
        debug: {}
      }
    }

    ctx.W = windowType.width.value
    ctx.H = windowType.height.value
    if (!optExShadingType) {
      // No external shading
      ctx.log(`No external shading. Full window is lit/clear.`)
      ctx.rect_clear = Rect.createFromPoints(0, 0, ctx.W, ctx.H)
      let res = {
        Rect_clear: ctx.rect_clear,
        debug: {
        }
      }
      ctx.endSection()
      return res;
    }

    ctx.log(`Applying external shading fins`)
    ctx.rect_clear = Rect.createFromPoints(0, 0, ctx.W, ctx.H)

    // Height of sunlight portion of the window
    if (optExShadingType.hasTopFin()) {
      ctx.topFinDepth = optExShadingType.horizontalFinDepth.getValueInUnits(Units.ft)
      ctx.topFinDist = optExShadingType.horizontalFinDist.getValueInUnits(Units.ft)
      // For simplicity, we just assume that the solar altitude completely determines the
      // angle here. In reality, the angle is a function of the solar azimuth and the
      // surface azimuth. But this is a good approximation.
      ctx.shadowLength = ctx.eval('topFinDepth*tan(solar_altitude_rads)', {
        solar_altitude_rads: solarAngles.solar_altitude_rads,
      }, 'shadowLength')
      ctx.shadedHeight = ctx.eval('clamp(shadowLength - topFinDist, 0, H)', {
        clamp: clamp,
      }, 'shadedHeight')
      ctx.rect_clear.setY1Y2(0, ctx.H - ctx.shadedHeight)
    }

    // Account for vertical fins
    // Note - the "right" fin is when facing the window from the outside. The "isSunToLeft" to when
    // facing the window from the inside / along the window's normal.
    if (optExShadingType.hasRightFin() && solarAngles.isSunToLeft()) {
      // Sun is to the left/counter-clockwise of us. Use right fin (right side on window, when facing the window)
      ctx.log(`Using right fin - surface_azimuth_diff_rads (${solarAngles.surface_azimuth_diff_rads}) <= 0`)
      ctx.verticalFinDepth = optExShadingType.rightFinDepth.getValueInUnits(Units.ft)
      ctx.verticalFinDist = optExShadingType.rightFinDist.getValueInUnits(Units.ft)

      ctx.shadowLength = ctx.eval('verticalFinDepth*abs(tan(surface_azimuth_diff_rads))', {
        surface_azimuth_diff_rads: solarAngles.surface_azimuth_diff_rads,
      }, 'shadowLength')
      ctx.shadedWidth = ctx.eval('clamp(shadowLength - verticalFinDist, 0, W)', {
        clamp: clamp,
      }, 'shadedWidth')

      // Sun is to the left/counter-clockwise of us. Use right fin (right side on window, when facing the window
      // from outside)
      ctx.rect_clear.setX1X2(ctx.shadedWidth, ctx.W)
    } else if (optExShadingType.hasLeftFin() && solarAngles.isSunToRight()) {
      // Sun is to the right/clockwise of us. Use left fin (left side on window, when facing the window)
      ctx.log(`Using left fin - surface_azimuth_diff_rads (${solarAngles.surface_azimuth_diff_rads}) > 0`)
      ctx.verticalFinDepth = optExShadingType.leftFinDepth.getValueInUnits(Units.ft)
      ctx.verticalFinDist = optExShadingType.leftFinDist.getValueInUnits(Units.ft)

      ctx.shadowLength = ctx.eval('verticalFinDepth*abs(tan(surface_azimuth_diff_rads))', {
        surface_azimuth_diff_rads: solarAngles.surface_azimuth_diff_rads,
      }, 'shadowLength')
      ctx.shadedWidth = ctx.eval('clamp(shadowLength - verticalFinDist, 0, W)', {
        clamp: clamp,
      }, 'shadedWidth')

      // Sun is to the right/clockwise of us. Use left fin (left side on window, when facing the window
      // from outside)
      ctx.rect_clear.setX1X2(0, ctx.W - ctx.shadedWidth)
    }

    let res = {
      Rect_clear: ctx.rect_clear,
      debug: {
      }
    }
    ctx.endSection()
    return res;
  }

  calcWindowShadedAreas(windowOrDoor, solarAngles, hr) {
    let ctx = this.ctx;
    ctx.startSection(`${windowOrDoor.getTypeName()} Shaded Areas`)

    let windowType = windowOrDoor.getWindowType()
    let optInShadingType = windowOrDoor.getInteriorShadingType()
    let optInShadingSchedule = windowOrDoor.getInteriorShadingSchedule()
    let optExShadingType = windowOrDoor.getExteriorShadingType()
    ctx.log(`Window type: ${windowType.name.value}`)
    ctx.log(`Internal shading type: ${optInShadingType ? optInShadingType.name.value : 'None'}`)
    ctx.log(`Internal shading schedule: ${optInShadingSchedule ? optInShadingSchedule.name.value : 'None'}`)
    ctx.log(`External shading type: ${optExShadingType ? optExShadingType.name.value : 'None'}`)

    // Note: the rectangles are based on looking at the window from the inside.
    let exteriorShadingRects = this.calcExteriorShadingAreas(windowOrDoor, solarAngles)
    let interiorShadingRects = this.calcInteriorShadingAreas(windowOrDoor, hr)

    // Diffuse clear: total window area, without interior shading
    // Diffuse shaded: total window area, with interior shading
    ctx.W = windowType.width.value;
    ctx.H = windowType.height.value;
    ctx.A_diffuse_clear = interiorShadingRects.Rect_clear.getArea();
    ctx.A_diffuse_shaded = ctx.W*ctx.H - ctx.A_diffuse_clear;

    // Direct means: the "effective"/lit window area, once exterior shading is applied
    // Direct clear: the lit window area, without interior shading
    // Direct shaded: the lit window area, with interior shading
    ctx.A_direct_clear = Rect.intersect(
      exteriorShadingRects.Rect_clear, interiorShadingRects.Rect_clear).getArea();
    ctx.A_direct_shaded = exteriorShadingRects.Rect_clear.getArea() - ctx.A_direct_clear;

    let fracGlass = windowOrDoor.getPercentGlass() / 100.0
    if (fracGlass < 1) {
      ctx.log("Fraction of glass < 1. Adjusting areas")
      ctx.fracGlass = fracGlass
      ctx.A_diffuse_clear *= ctx.fracGlass
      ctx.A_diffuse_shaded *= ctx.fracGlass
      ctx.A_direct_clear *= ctx.fracGlass
      ctx.A_direct_shaded *= ctx.fracGlass
    }

    let res = {
      A_diffuse_clear: ctx.A_diffuse_clear,
      A_diffuse_shaded: ctx.A_diffuse_shaded,
      A_direct_clear: ctx.A_direct_clear,
      A_direct_shaded: ctx.A_direct_shaded,
      debug: {
        ...exteriorShadingRects.debug,
        ...interiorShadingRects,
      }
    }
    ctx.endSection()
    return res
  }

  OLD_calcWindowShadedAreas(windowOrDoor, solarAngles, hr) {
    /**
     * This is the old code for calculating this. Likely contains errors.
     */

    let ctx = this.ctx;
    ctx.startSection(`${windowOrDoor.getTypeName()} Shaded Areas`)

    let windowType = windowOrDoor.getWindowType()
    let optInShadingType = windowOrDoor.getInteriorShadingType()
    let optInShadingSchedule = windowOrDoor.getInteriorShadingSchedule()
    let optExShadingType = windowOrDoor.getExteriorShadingType()

    ctx.log(`Window type: ${windowType.name.value}`)
    ctx.log(`Internal shading type: ${optInShadingType ? optInShadingType.name.value : 'None'}`)
    ctx.log(`External shading type: ${optExShadingType ? optExShadingType.name.value : 'None'}`)

    ctx.W = windowType.width.value
    ctx.H = windowType.height.value

    if (!optExShadingType) {
      // No external shading
      ctx.log(`No external shading. Setting H_SL = H, W_SL = W`)
      ctx.H_SL = ctx.H
      ctx.W_SL = ctx.W
    } else {
      ctx.log(`Calculating H_SL and W_SL for external shading`)
      // Height of sunlight portion of the window
      ctx.P_H = optExShadingType.horizontalFinDepth.getValueInUnits(Units.ft)
      ctx.R_H = optExShadingType.horizontalFinDist.getValueInUnits(Units.ft)

      // Calculate Omega_V_rads
      ctx.Omega_V_rads = ctx.eval('arctan(tan(solar_altitude_rads) / cos(surface_azimuth_diff_rads))', {
        arctan: Math.atan,
        solar_altitude_rads: solarAngles.solar_altitude_rads,
        surface_azimuth_diff_rads: solarAngles.surface_azimuth_diff_rads,
      }, 'Omega_V_rads')
      ctx.S_H = ctx.eval('P_H*tan(Omega_V_rads)', {
      }, 'S_H')
      ctx.H_SL = ctx.eval('max(0, min(H, H - (S_H - R_H)))', {
      }, 'H_SL')
      // Width of sunlit portion of the window
      if (solarAngles.surface_azimuth_diff_rads <= 0) {
        // Use right fin
        ctx.log(`Using right fin - surface_azimuth_diff_rads (${solarAngles.surface_azimuth_diff_rads}) <= 0`)
        ctx.P_W = optExShadingType.rightFinDepth.getValueInUnits(Units.ft)
        ctx.R_W = optExShadingType.rightFinDist.getValueInUnits(Units.ft)
      } else {
        // Use left fin
        ctx.log(`Using left fin - surface_azimuth_diff_rads (${solarAngles.surface_azimuth_diff_rads}) > 0`)
        ctx.P_W = optExShadingType.leftFinDepth.getValueInUnits(Units.ft)
        ctx.R_W = optExShadingType.leftFinDist.getValueInUnits(Units.ft)
      }
      ctx.S_W = ctx.eval('P_W*abs(tan(surface_azimuth_diff_rads))', {
        surface_azimuth_diff_rads: solarAngles.surface_azimuth_diff_rads,
      }, 'S_W')
      ctx.W_SL = ctx.eval('max(0, min(W, W - (S_W - R_W)))', {
      }, 'W_SL')
    }

    // Calc the 4 result areas
    let isHorizontal = optInShadingType !== null && optInShadingType.orientation.value === ShadesOrientation.Horizontal
    if (optInShadingType !== null && optInShadingSchedule !== null) {
      ctx.log(`Using internal shading schedule`)
      ctx.full_sched = optInShadingSchedule.getData();
      ctx.sched_hr = ctx.full_sched[hr]
    } else {
      ctx.log("No schedule, using value 1.0 (always on)")
      ctx.sched_hr = 1.0;
    }
    if (isHorizontal) {
      ctx.log("Calculating shaded areas - horizontal internal shading (or none)")
      ctx.H_clear = ctx.eval('H*(1.0 - sched_hr)', {
      }, 'H_clear')
      ctx.A_clear_light = ctx.eval('min(H_clear, H_SL)*W_SL', {
      }, 'A_clear_light')
      ctx.A_clear_shade = ctx.eval('max(0, H_clear - H_SL)*W + min(H_clear,H_SL)*(W - W_SL)', {
      }, 'A_clear_shade')
      ctx.A_shade_light = ctx.eval('max(0, H_SL-H_clear)*W_SL', {
      }, 'A_shade_light')
      ctx.A_shade_shade = ctx.eval('(H - max(H_SL,H_clear))*W + max(0, H_SL - H_clear)*(W - W_SL)', {
      }, 'A_shade_shade')
    } else {
      ctx.log("Calculating shaded areas - vertical internal shading")
      ctx.W_clear = ctx.eval('W*(1.0 - sched_hr)', {
      }, 'W_clear')
      ctx.A_clear_light = ctx.eval('H_SL*(W_SL - 0.5*(W - W_clear) - max(0, 0.5*(W - W_clear) - (W - W_SL)))', {
      }, 'A_clear_light')
      ctx.A_clear_shade = ctx.eval('(H - H_SL)*(W_SL - 0.5*(W - W_clear) - ' +
        'max(0, 0.5*(W - W_clear) - (W - W_SL))) + ' +
        'H*(max(0, (W - W_SL) - 0.5*(W - W_clear)))', {
      }, 'A_clear_shade')
      ctx.A_shade_light = ctx.eval('H_SL*(0.5*(W - W_clear) + max(0, 0.5*(W - W_clear) - (W - W_SL)))', {
      }, 'A_shade_light')
      ctx.A_shade_shade = ctx.eval('H*min(0.5*(W - W_clear), W - W_SL) + ' + 
        '(H - H_SL)*(0.5*(W - W_clear)+max(0, 0.5*(W - W_clear) - (W - W_SL)))', {
      }, 'A_shade_shade')
    }

    let fracGlass = windowOrDoor.getPercentGlass() / 100.0
    if (fracGlass < 1) {
      ctx.log("Fraction of glass < 1. Adjusting areas")
      ctx.fracGlass = fracGlass
      ctx.A_clear_light *= ctx.fracGlass
      ctx.A_clear_shade *= ctx.fracGlass
      ctx.A_shade_light *= ctx.fracGlass
      ctx.A_shade_shade *= ctx.fracGlass
    }

    let res = {
      A_clear_light: ctx.A_clear_light,
      A_shade_light: ctx.A_shade_light,
      A_clear_shade: ctx.A_clear_shade,
      A_shade_shade: ctx.A_shade_shade,
    }
    ctx.endSection()
    return res
  }
}